import { useState } from "react";
import {
  useS3Imports,
  type S3ImportConfig,
  type S3ImportCreateInput,
} from "../hooks/useS3Imports";
import { useJobTypes } from "../hooks/useJobTypes";
import { EmptyState } from "@/components/EmptyState";
import { ConfirmModal } from "@/components/ConfirmModal";
import { showApiError } from "@/lib/error-toast";

// E-023 — admin UI for the S3/R2 import source ("cloud hot folder").
// Mirrors HotFolderPanel's create/edit-card shape. The secret access key is
// write-only (the API never returns it), so the card edit form leaves it blank
// and only sends a new value when the operator types one.

interface S3ImportFormData {
  name: string;
  bucket: string;
  prefix: string;
  processedPrefix: string;
  region: string;
  endpoint: string;
  accessKeyId: string;
  secretAccessKey: string;
  forcePathStyle: boolean;
  jobTypeId: string;
  routeByContent: boolean;
  pollingIntervalSec: number;
}

const DEFAULT_FORM: S3ImportFormData = {
  name: "",
  bucket: "",
  prefix: "",
  processedPrefix: "processed/",
  region: "",
  endpoint: "",
  accessKeyId: "",
  secretAccessKey: "",
  forcePathStyle: false,
  jobTypeId: "",
  routeByContent: false,
  pollingIntervalSec: 60,
};

function toCreateInput(form: S3ImportFormData): S3ImportCreateInput {
  return {
    name: form.name,
    bucket: form.bucket,
    prefix: form.prefix,
    processedPrefix: form.processedPrefix,
    region: form.region || null,
    endpoint: form.endpoint || null,
    accessKeyId: form.accessKeyId,
    secretAccessKey: form.secretAccessKey,
    forcePathStyle: form.forcePathStyle,
    jobTypeId: form.jobTypeId || null,
    routeByContent: form.routeByContent,
    pollingIntervalSec: form.pollingIntervalSec,
  };
}

export function S3ImportPanel() {
  const { imports, isLoading, error, create, update, remove, toggle, test } = useS3Imports();
  const { items: jobTypes } = useJobTypes();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<S3ImportFormData>(DEFAULT_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  const setField = (key: keyof S3ImportFormData, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    setIsCreating(true);
    try {
      await create(toCreateInput(form));
      setShowCreate(false);
      setForm(DEFAULT_FORM);
      setTestResult(null);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setIsCreating(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await test({
        bucket: form.bucket,
        prefix: form.prefix,
        region: form.region || null,
        endpoint: form.endpoint || null,
        accessKeyId: form.accessKeyId,
        secretAccessKey: form.secretAccessKey,
        forcePathStyle: form.forcePathStyle,
      });
      setTestResult(result);
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof Error ? e.message : "Test failed" });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return <div className="admin-loading"><div className="loading-spinner" /></div>;
  }

  return (
    <div className="panel-content">
      <div className="export-tab-header">
        <h3 className="rb-section-title">S3 / R2 Import Sources</h3>
        <button className="rb-btn rb-btn-save" onClick={() => setShowCreate(true)}>
          + Add Import Source
        </button>
      </div>

      <p className="rb-section-desc">
        Poll an S3 or Cloudflare R2 bucket prefix and ingest every new object as a batch
        — the cloud-native hot folder. Works on Railway where the worker and API run in
        separate containers (both reach the bucket over the network). Processed objects
        are moved to a <code>processed/</code> prefix so they aren&rsquo;t re-ingested.
      </p>

      {error && <div className="admin-error">{error}</div>}

      {showCreate && (
        <form className="export-create-form" onSubmit={handleCreate} style={{ marginBottom: 16 }}>
          <h4 style={{ margin: "0 0 12px", fontWeight: 600 }}>New Import Source</h4>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Name</label>
            <input className="rb-input" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="R2 Invoices Drop" required />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Bucket</label>
            <input className="rb-input" value={form.bucket} onChange={(e) => setField("bucket", e.target.value)} placeholder="my-drop-bucket" required />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Watch Prefix</label>
            <input className="rb-input" value={form.prefix} onChange={(e) => setField("prefix", e.target.value)} placeholder="inbound/ (empty = whole bucket)" />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Processed Prefix</label>
            <input className="rb-input" value={form.processedPrefix} onChange={(e) => setField("processedPrefix", e.target.value)} placeholder="processed/ (empty = delete)" />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Endpoint</label>
            <input className="rb-input" value={form.endpoint} onChange={(e) => setField("endpoint", e.target.value)} placeholder="https://<account>.r2.cloudflarestorage.com (blank for AWS S3)" />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Region</label>
            <input className="rb-input" value={form.region} onChange={(e) => setField("region", e.target.value)} placeholder="auto (R2) / us-east-1" />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Access Key ID</label>
            <input className="rb-input" value={form.accessKeyId} onChange={(e) => setField("accessKeyId", e.target.value)} autoComplete="off" required />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Secret Access Key</label>
            <input className="rb-input" type="password" value={form.secretAccessKey} onChange={(e) => setField("secretAccessKey", e.target.value)} autoComplete="off" required />
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Job Type</label>
            <select className="rb-select" value={form.jobTypeId} onChange={(e) => setField("jobTypeId", e.target.value)}>
              <option value="">Route by content (no fixed type)</option>
              {jobTypes
                .filter((jt) => jt.isActive && jt.currentPublishedVersionId)
                .map((jt) => <option key={jt.id} value={jt.id}>{jt.name}</option>)}
            </select>
          </div>

          <div className="rb-row" style={{ marginBottom: 10 }}>
            <label className="rb-label">Polling Interval (sec)</label>
            <input className="rb-input" type="number" min={10} max={86400} value={form.pollingIntervalSec} onChange={(e) => setField("pollingIntervalSec", Number(e.target.value))} required />
          </div>

          <div className="rb-row" style={{ marginBottom: 4, alignItems: "center", gap: 8 }}>
            <label className="rb-label" htmlFor="s3-create-route-by-content">Route by content</label>
            <input id="s3-create-route-by-content" type="checkbox" checked={form.routeByContent} onChange={(e) => setField("routeByContent", e.target.checked)} />
          </div>
          <p className="rb-hint" style={{ marginBottom: 10 }}>
            Route by content (ignore Job Type) — auto-route every object by its fingerprint.
          </p>

          {testResult && (
            <div className={testResult.ok ? "import-result" : "admin-error"} style={{ marginBottom: 10 }}>
              {testResult.ok ? <span className="import-result-ok">Connection OK:</span> : "Connection failed: "}{" "}
              {testResult.message}
            </div>
          )}
          {createError && <div className="admin-error">{createError}</div>}

          <div className="rb-row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button type="button" className="rb-btn" onClick={handleTest} disabled={isTesting || !form.bucket || !form.accessKeyId || !form.secretAccessKey}>
              {isTesting ? "Testing…" : "Test Connection"}
            </button>
            <button type="button" className="rb-btn" onClick={() => { setShowCreate(false); setCreateError(null); setTestResult(null); }}>
              Cancel
            </button>
            <button type="submit" className="rb-btn rb-btn-save" disabled={isCreating}>
              {isCreating ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      )}

      {imports.length === 0 && !showCreate && (
        <EmptyState
          title="No S3 / R2 import sources"
          body={"Connect a cloud bucket as a drop location. New objects under the watched prefix are ingested automatically and moved aside once processed. Use “+ Add Import Source” above."}
        />
      )}

      {imports.map((cfg) => (
        <S3ImportCard
          key={cfg.id}
          config={cfg}
          jobTypes={jobTypes}
          onUpdate={update}
          onRemove={remove}
          onToggle={toggle}
        />
      ))}
    </div>
  );
}

// ─── Import Source Card ──────────────────────────────────────────

function S3ImportCard({
  config,
  jobTypes,
  onUpdate,
  onRemove,
  onToggle,
}: {
  config: S3ImportConfig;
  jobTypes: Array<{ id: string; name: string; isActive?: boolean; currentPublishedVersionId?: string | null }>;
  onUpdate: ReturnType<typeof useS3Imports>["update"];
  onRemove: ReturnType<typeof useS3Imports>["remove"];
  onToggle: ReturnType<typeof useS3Imports>["toggle"];
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<S3ImportFormData>({
    name: config.name,
    bucket: config.bucket,
    prefix: config.prefix,
    processedPrefix: config.processedPrefix,
    region: config.region ?? "",
    endpoint: config.endpoint ?? "",
    accessKeyId: config.accessKeyId,
    secretAccessKey: "",
    forcePathStyle: config.forcePathStyle,
    jobTypeId: config.jobTypeId ?? "",
    routeByContent: config.routeByContent,
    pollingIntervalSec: config.pollingIntervalSec,
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const setEditField = (key: keyof S3ImportFormData, value: unknown) =>
    setEditForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError(null);
    setIsSaving(true);
    try {
      await onUpdate(config.id, {
        name: editForm.name,
        bucket: editForm.bucket,
        prefix: editForm.prefix,
        processedPrefix: editForm.processedPrefix,
        region: editForm.region || null,
        endpoint: editForm.endpoint || null,
        accessKeyId: editForm.accessKeyId,
        // Only send a new secret when the operator typed one (write-only field).
        ...(editForm.secretAccessKey ? { secretAccessKey: editForm.secretAccessKey } : {}),
        forcePathStyle: editForm.forcePathStyle,
        jobTypeId: editForm.jobTypeId || null,
        routeByContent: editForm.routeByContent,
        pollingIntervalSec: editForm.pollingIntervalSec,
      });
      setIsEditing(false);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await onToggle(config.id);
    } catch (e) {
      showApiError(e, { fallback: "Toggle failed" });
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    try {
      await onRemove(config.id);
    } catch (e) {
      showApiError(e, { fallback: "Delete failed" });
    }
  };

  const jobTypeName = config.routeByContent
    ? "Route by content"
    : jobTypes.find((j) => j.id === config.jobTypeId)?.name ?? (config.jobTypeId || "Route by content");

  return (
    <div className={`export-dest-card ${!config.isEnabled ? "export-dest-card--inactive" : ""}`}>
      <div className="export-dest-header" onClick={() => setIsExpanded((v) => !v)}>
        <div className="export-dest-info">
          <span className="export-dest-name">{config.name}</span>
          <span className={`jt-badge ${config.isEnabled ? "jt-badge--active" : "jt-badge--inactive"}`}>
            {config.isEnabled ? "Polling" : "Paused"}
          </span>
          <span className="export-dest-type">{config.bucket}/{config.prefix}</span>
        </div>
        <div className="export-dest-actions" onClick={(e) => e.stopPropagation()}>
          <button className="rb-btn" onClick={handleToggle} disabled={isToggling}>
            {config.isEnabled ? "Pause" : "Resume"}
          </button>
          <button className="rb-btn" onClick={() => { setIsEditing(true); setIsExpanded(true); }}>Edit</button>
          <button className="rb-btn rb-btn-danger" onClick={() => setConfirmDeleteOpen(true)}>Delete</button>
        </div>
      </div>

      <ConfirmModal
        open={confirmDeleteOpen}
        title={`Delete import source "${config.name}"?`}
        description="This stops polling and removes the configuration. Objects already in the bucket are untouched."
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {isExpanded && !isEditing && (
        <div className="export-dest-detail">
          <div className="detail-grid">
            <span className="detail-label">Bucket</span><span>{config.bucket}</span>
            <span className="detail-label">Watch Prefix</span><span>{config.prefix || "(whole bucket)"}</span>
            <span className="detail-label">Processed</span><span>{config.processedPrefix || "(delete)"}</span>
            <span className="detail-label">Endpoint</span><span>{config.endpoint || "AWS S3 default"}</span>
            <span className="detail-label">Region</span><span>{config.region || "us-east-1"}</span>
            <span className="detail-label">Job Type</span><span>{jobTypeName}</span>
            <span className="detail-label">Poll</span><span>{config.pollingIntervalSec}s</span>
          </div>
        </div>
      )}

      {isExpanded && isEditing && (
        <div className="export-dest-detail">
          <form onSubmit={handleSave}>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Name</label><input className="rb-input" value={editForm.name} onChange={(e) => setEditField("name", e.target.value)} required /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Bucket</label><input className="rb-input" value={editForm.bucket} onChange={(e) => setEditField("bucket", e.target.value)} required /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Watch Prefix</label><input className="rb-input" value={editForm.prefix} onChange={(e) => setEditField("prefix", e.target.value)} placeholder="inbound/" /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Processed Prefix</label><input className="rb-input" value={editForm.processedPrefix} onChange={(e) => setEditField("processedPrefix", e.target.value)} placeholder="processed/" /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Endpoint</label><input className="rb-input" value={editForm.endpoint} onChange={(e) => setEditField("endpoint", e.target.value)} placeholder="(blank for AWS)" /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Region</label><input className="rb-input" value={editForm.region} onChange={(e) => setEditField("region", e.target.value)} placeholder="auto / us-east-1" /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Access Key ID</label><input className="rb-input" value={editForm.accessKeyId} onChange={(e) => setEditField("accessKeyId", e.target.value)} autoComplete="off" /></div>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Secret Access Key</label>
              <input className="rb-input" type="password" value={editForm.secretAccessKey} onChange={(e) => setEditField("secretAccessKey", e.target.value)} placeholder="•••••• (leave blank to keep)" autoComplete="off" />
            </div>
            <div className="rb-row" style={{ marginBottom: 10 }}>
              <label className="rb-label">Job Type</label>
              <select className="rb-select" value={editForm.jobTypeId} onChange={(e) => setEditField("jobTypeId", e.target.value)}>
                <option value="">Route by content (no fixed type)</option>
                {jobTypes.filter((jt) => jt.isActive !== false && jt.currentPublishedVersionId).map((jt) => <option key={jt.id} value={jt.id}>{jt.name}</option>)}
              </select>
            </div>
            <div className="rb-row" style={{ marginBottom: 10 }}><label className="rb-label">Polling (sec)</label><input className="rb-input" type="number" min={10} max={86400} value={editForm.pollingIntervalSec} onChange={(e) => setEditField("pollingIntervalSec", Number(e.target.value))} /></div>
            <div className="rb-row" style={{ marginBottom: 4, alignItems: "center", gap: 8 }}>
              <label className="rb-label" htmlFor={`s3-edit-route-${config.id}`}>Route by content</label>
              <input id={`s3-edit-route-${config.id}`} type="checkbox" checked={editForm.routeByContent} onChange={(e) => setEditField("routeByContent", e.target.checked)} />
            </div>
            {editError && <div className="admin-error">{editError}</div>}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <button type="button" className="rb-btn" onClick={() => setIsEditing(false)}>Cancel</button>
              <button type="submit" className="rb-btn rb-btn-save" disabled={isSaving}>{isSaving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
